"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { List, Menu, X } from "lucide-react";
import { cn } from "~/lib/utils";
import {
  DEFAULT_DOCS_SECTION_ID,
  DOCS_NAV_GROUPS,
  getDocsSection,
  type DocsSectionBlock,
} from "~/lib/inbound-gpt/documentation-content";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { adaptMarketingCopyForBrand } from "~/lib/siteglue/adapt-marketing-copy";
import type { MarketingBrandId } from "~/lib/marketing-brand";

function adaptBlock(block: DocsSectionBlock, brand: MarketingBrandId): DocsSectionBlock {
  const a = (s: string) => adaptMarketingCopyForBrand(s, brand);
  switch (block.type) {
    case "paragraph":
      return { ...block, text: a(block.text) };
    case "list":
      return { ...block, items: block.items.map(a) };
    case "steps":
      return {
        ...block,
        items: block.items.map((step) => ({
          title: a(step.title),
          body: a(step.body),
        })),
      };
    case "callout":
      return { ...block, title: a(block.title), text: a(block.text) };
    case "links":
      return {
        ...block,
        items: block.items.map((link) => ({
          ...link,
          label: a(link.label),
          description: link.description ? a(link.description) : link.description,
        })),
      };
    default:
      return block;
  }
}

function DocsBlock({ block }: { block: DocsSectionBlock }) {
  switch (block.type) {
    case "paragraph":
      return <p className="text-base leading-relaxed text-muted-foreground">{block.text}</p>;
    case "list":
      return (
        <ul className="space-y-2.5">
          {block.items.map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-base text-muted-foreground">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "steps":
      return (
        <ol className="space-y-5">
          {block.items.map((step, index) => (
            <li key={step.title} className="flex gap-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-semibold text-primary">
                {index + 1}
              </span>
              <div>
                <h3 className="text-base font-semibold text-foreground">{step.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      );
    case "callout":
      return (
        <div className="rounded-xl border border-primary/20 bg-primary/5 px-5 py-4">
          <p className="text-sm font-semibold text-primary">{block.title}</p>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{block.text}</p>
        </div>
      );
    case "links":
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          {block.items.map((link) => (
            <Link
              key={link.href + link.label}
              href={link.href}
              className="rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:border-primary/40 hover:bg-primary/5"
            >
              <span className="text-sm font-semibold text-primary">{link.label}</span>
              {link.description ? (
                <p className="mt-1 text-xs text-muted-foreground">{link.description}</p>
              ) : null}
            </Link>
          ))}
        </div>
      );
    default:
      return null;
  }
}

export function DocumentationPageContent() {
  const chrome = getMarketingChrome();
  const brand = chrome.brandId;
  const a = (s: string) => adaptMarketingCopyForBrand(s, brand);
  const [activeId, setActiveId] = useState(DEFAULT_DOCS_SECTION_ID);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const section = useMemo(() => getDocsSection(activeId), [activeId]);
  const adaptedSection = useMemo(
    () => ({
      ...section,
      title: a(section.title),
      description: a(section.description),
      blocks: section.blocks.map((block) => adaptBlock(block, brand)),
      toc: section.toc.map((item) => ({ ...item, label: a(item.label) })),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [section, brand],
  );
  const navGroups = useMemo(
    () =>
      DOCS_NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.map((item) => ({
          ...item,
          label: a(item.label),
        })),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brand],
  );

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (hash && getDocsSection(hash).id === hash) {
      setActiveId(hash);
    }
  }, []);

  const selectSection = (id: string) => {
    setActiveId(id);
    setMobileNavOpen(false);
    window.history.replaceState(null, "", `#${id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] border-t border-border bg-background">
      <div className="mx-auto flex max-w-[1400px]">
        {/* Left sidebar */}
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-64 shrink-0 overflow-y-auto border-r border-border px-4 py-8 lg:block xl:w-72">
          <p className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Documentation
          </p>
          <nav className="mt-6 space-y-6" aria-label="Documentation">
            {navGroups.map((group) => (
              <div key={group.title}>
                <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                  {group.title}
                </p>
                <ul className="mt-2 space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = item.id === activeId;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => selectSection(item.id)}
                          className={cn(
                            "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
                            active
                              ? "bg-primary/10 font-medium text-primary"
                              : "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
                          )}
                        >
                          <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                          <span>{item.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main + right TOC */}
        <div className="flex min-w-0 flex-1">
          <main className="min-w-0 flex-1 px-4 py-8 sm:px-8 lg:px-10 lg:py-10">
            <div className="mb-6 flex items-center justify-between gap-3 lg:hidden">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium"
              >
                <Menu className="h-4 w-4" />
                Docs menu
              </button>
            </div>

            <p className="text-sm font-semibold text-primary">
              {navGroups.find((g) => g.items.some((i) => i.id === activeId))?.title ??
                "Documentation"}
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
              {adaptedSection.title}
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
              {adaptedSection.description}
            </p>

            <div className="mt-10 space-y-8">
              {adaptedSection.toc.length > 0 ? (
                <div className="space-y-2 xl:hidden">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    On this page
                  </p>
                  <ul className="flex flex-wrap gap-x-4 gap-y-1">
                    {adaptedSection.toc.map((item) => (
                      <li key={item.id}>
                        <a href={`#${item.id}`} className="text-sm text-primary hover:underline">
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {adaptedSection.blocks.map((block, index) => {
                const tocItem = adaptedSection.toc[index];
                return (
                  <div
                    key={`${adaptedSection.id}-${index}`}
                    id={tocItem?.id}
                    className={tocItem ? "scroll-mt-24" : undefined}
                  >
                    {tocItem ? (
                      <h2 className="mb-3 text-xl font-semibold tracking-tight">{tocItem.label}</h2>
                    ) : null}
                    <DocsBlock block={block} />
                  </div>
                );
              })}
            </div>

            <div className="mt-16 border-t border-border pt-8">
              <p className="text-sm text-muted-foreground">
                Need more help? Visit the{" "}
                <Link href="/help-center" className="font-medium text-primary hover:underline">
                  Help Center
                </Link>
                ,{" "}
                <Link href="/api-reference" className="font-medium text-primary hover:underline">
                  API Reference
                </Link>
                , or{" "}
                <Link
                  href="/custom-agent-development"
                  className="font-medium text-primary hover:underline"
                >
                  request custom agent development
                </Link>
                .
              </p>
            </div>
          </main>

          {/* Right TOC */}
          <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-52 shrink-0 overflow-y-auto py-10 pr-6 xl:block">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <List className="h-3.5 w-3.5" />
              On this page
            </div>
            <ul className="mt-4 space-y-2.5">
              {adaptedSection.toc.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileNavOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close menu"
            onClick={() => setMobileNavOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 flex w-[min(100vw-3rem,20rem)] flex-col bg-background shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="text-sm font-semibold">Documentation</span>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="rounded-md p-2 text-muted-foreground hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4">
              {navGroups.map((group) => (
                <div key={group.title} className="mb-5">
                  <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.title}
                  </p>
                  <ul className="mt-2 space-y-0.5">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const active = item.id === activeId;
                      return (
                        <li key={item.id}>
                          <button
                            type="button"
                            onClick={() => selectSection(item.id)}
                            className={cn(
                              "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm",
                              active
                                ? "bg-primary/10 font-medium text-primary"
                                : "text-muted-foreground hover:bg-muted/70",
                            )}
                          >
                            <Icon className="h-4 w-4 shrink-0" />
                            {item.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </div>
      ) : null}
    </div>
  );
}
